import { inferenceHttp } from '@/lib/api/http-client';
import { ArtifactExportsService, CreateArtifactExportDto } from '@inference.engine/api-client/web';
import type { ArtifactExportAssetDto } from './artifact-exports.api.types';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

// Type guard helper to check if asset has the expected structure
export function isArtifactExportAsset(asset: unknown): asset is ArtifactExportAssetDto {
  return (
    typeof asset === 'object' &&
    asset !== null &&
    'assetId' in asset &&
    'mimeType' in asset &&
    typeof (asset as { assetId: unknown }).assetId === 'string' &&
    typeof (asset as { mimeType: unknown }).mimeType === 'string'
  );
}

/**
 * Gets the last artifact export by reference key
 * @param artifactId - The artifact ID
 * @param referenceKey - Optional reference key for the export
 * @returns The artifact export DTO, or null if not found
 */
export async function getArtifactExportLast(artifactId: string, referenceKey?: string) {
  try {
    const response = await ArtifactExportsService.getLast({
      client: inferenceHttp,
      path: { artifactId },
      query: referenceKey ? { referenceKey } : undefined,
      headers: {
        // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true',
      },
    });

    return response.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Gets the status of an artifact export
 * @param artifactId - The artifact ID
 * @param exportId - The export ID
 * @returns The artifact export status DTO
 */
export async function getArtifactExportStatus(artifactId: string, exportId: string) {
  const response = await ArtifactExportsService.getStatus({
    client: inferenceHttp,
    path: { artifactId, exportId },
  });

  if (!response.data) {
    throw new Error('Failed to get artifact export status');
  }

  return response.data;
}

/**
 * Gets the asset for an artifact export
 * @param artifactId - The artifact ID
 * @param exportId - The export ID
 * @returns The artifact export asset DTO
 */
export async function getArtifactExportAsset(artifactId: string, exportId: string) {
  const response = await ArtifactExportsService.getAsset({
    client: inferenceHttp,
    path: { artifactId, exportId },
  });

  if (!response.data) {
    throw new Error('Failed to get artifact export asset');
  }

  return response.data;
}

/**
 * Creates a new artifact export
 * @param artifactId - The artifact ID
 * @param body - Optional export configuration
 * @returns The created artifact export DTO
 */
export async function createArtifactExport(artifactId: string, body?: CreateArtifactExportDto) {
  const response = await ArtifactExportsService.create({
    client: inferenceHttp,
    path: { artifactId },
    body: body ?? {},
  });

  if (!response.data) {
    throw new Error('Failed to create artifact export');
  }

  return response.data;
}
